// src/components/navigation/AudioMuteButton.js
import React from "react";
import { useTranslation } from "react-i18next";

/* ------------------------------------------------------------------ */
/* AudioMuteButton – icon-only mute toggle for spoken instructions     */
/* ------------------------------------------------------------------ */
/**
 * Icon-only navbar toggle for the recording pod's spoken instructions. Styled to
 * match the adjacent Guide CTA, minus the label.
 *
 * The icon shows the CURRENT state (speaker on / speaker crossed out) while the
 * accessible name describes the ACTION, so screen-reader users hear what a press
 * will do. Muting only affects audio: the on-screen instruction text a
 * participant follows is never suppressed.
 *
 * @param {object} props
 * @param {boolean} [props.muted=false]  Whether audio prompts are muted.
 * @param {Function} [props.onToggle]    Called when the user presses the button.
 * @param {string} [props.className]     Extra Tailwind classes.
 * @returns {JSX.Element}
 */
function AudioMuteButton({ muted = false, onToggle, className = "" }) {
  const { t } = useTranslation();

  const label = muted
    ? t("navBar.unmuteAudio", "Unmute audio instructions")
    : t("navBar.muteAudio", "Mute audio instructions");

  // self-stretch keeps this the same height as the labelled controls beside it.
  // They take their height from a text-lg line box (~28px); an icon-only button
  // has only the text-xs icon's line box, so it would otherwise sit visibly
  // shorter than the Guide CTA. Stretching follows whatever height the row
  // settles on rather than pinning a number that would drift from it.
  const buttonClassName = [
    "small-outlined-button group flex !flex-row items-center justify-center",
    "self-stretch !w-fit",
    className,
  ]
    .join(" ")
    .trim();

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={muted}
      aria-label={label}
      title={label}
      className={buttonClassName}
    >
      <span
        className="material-symbols-outlined text-primary text-xs
          group-hover:text-greyscale-1 group-focus-visible:text-greyscale-1"
        aria-hidden="true"
      >
        {muted ? "volume_off" : "volume_up"}
      </span>
    </button>
  );
}

export default React.memo(AudioMuteButton);
